// Document upload configuration
export const DOCUMENT_CONFIG = {
  // Storage settings
  BUCKET_NAME: 'documentation',
  CACHE_CONTROL: '3600',
  
  // File validation
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB in bytes
  MAX_FILE_SIZE_DISPLAY: '10MB',
  
  ALLOWED_FILE_TYPES: [
    'application/pdf',
    'image/jpeg',
    'image/jpg', 
    'image/png',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ] as const,
  
  ALLOWED_FILE_EXTENSIONS: [
    '.pdf',
    '.jpg',
    '.jpeg',
    '.png',
    '.doc',
    '.docx'
  ] as const,
  
  FILE_TYPE_DISPLAY: 'PDF, JPG, PNG, DOC, DOCX',
  
  // Folder structure
  FOLDER_SEPARATOR: '/',
  FALLBACK_FOLDER_NAME: 'unknown',
  
  // Document categories
  CATEGORIES: {
    CERTIFICATES: {
      id: 'certificates',
      label: 'Sertifikater',
      color: 'blue',
      icon: 'File'
    },
    MANUALS: {
      id: 'manuals', 
      label: 'Manualer',
      color: 'green',
      icon: 'File'
    },
    INSPECTION_REPORTS: {
      id: 'inspection_reports',
      label: 'Inspeksjonsrapporter', 
      color: 'purple',
      icon: 'File'
    }
  } as const
}

// Error messages in Norwegian
export const ERROR_MESSAGES = {
  NO_FILE_SELECTED: 'Ingen fil valgt',
  FILE_TOO_LARGE: `Filen er for stor (maks ${DOCUMENT_CONFIG.MAX_FILE_SIZE_DISPLAY})`,
  INVALID_FILE_TYPE: 'Filtype ikke støttet',
  UPLOAD_FAILED: 'Upload feilet',
  DELETE_FAILED: 'Sletting feilet',
  LIST_FAILED: 'Ukjent feil ved henting av dokumenter',
  UNKNOWN_ERROR: 'Ukjent feil'
} as const

// Success messages in Norwegian  
export const SUCCESS_MESSAGES = {
  UPLOAD_SUCCESS: 'Dokumentet ble lastet opp!',
  DELETE_SUCCESS: 'Dokumentet ble slettet'
} as const

// UI text in Norwegian
export const UI_TEXT = {
  // Upload dialog
  UPLOAD_DIALOG_TITLE: 'Last opp dokument',
  UPLOAD_BUTTON: 'Last opp dokument',
  DRAG_DROP_TEXT: 'Dra og slipp filen her, eller klikk for å velge',
  DROP_FILE_HERE: 'Slipp filen her',
  SELECT_FILE: 'Velg fil',
  UPLOADING: 'Laster opp...',
  CLOSE: 'Lukk',
  CANCEL: 'Avbryt',
  UNKNOWN_ERROR: 'Ukjent feil ved opplasting',
  
  // Document categories
  DOCUMENTS: 'Dokumenter',
  NO_DOCUMENTS_FOUND: 'Ingen dokumenter funnet',
  RECENT_ACTIVITY: 'Nylig aktivitet',
  
  // File actions
  VIEW: 'Vis',
  DOWNLOAD: 'Last ned',
  DELETE: 'Slett',
  
  // File info
  SUPPORTED_FORMATS: `Støttede formater: ${DOCUMENT_CONFIG.FILE_TYPE_DISPLAY}`,
  MAX_FILE_SIZE: `Maksimal filstørrelse: ${DOCUMENT_CONFIG.MAX_FILE_SIZE_DISPLAY}`,
  FILE_PATH_PREFIX: 'Filsti: documentation/',
  
  // Activity log examples (these could come from a database later)
  ACTIVITY_EXAMPLES: [
    { action: 'Produksjonssertifikat.pdf lagt til', time: '2 dager siden' },
    { action: 'Inspeksjonsrapport_2024-01.pdf oppdatert', time: '1 uke siden' }, 
    { action: 'CE-merking.pdf lastet ned', time: '2 uker siden' }
  ]
} as const

// Utility function to get file size display
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

// Utility function to check if file type is allowed
export const isFileTypeAllowed = (fileType: string): boolean => {
  return DOCUMENT_CONFIG.ALLOWED_FILE_TYPES.includes(fileType as any)
}

// Utility function to get category color classes
export const getCategoryColorClasses = (categoryColor: string) => {
  const colorMap = {
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200', 
      iconBg: 'bg-blue-100',
      iconText: 'text-blue-600'
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      iconBg: 'bg-green-100', 
      iconText: 'text-green-600'
    },
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      iconBg: 'bg-purple-100',
      iconText: 'text-purple-600'
    }
  }
  
  return colorMap[categoryColor as keyof typeof colorMap] || colorMap.blue
} 